import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  Brain,
  Eye,
  EyeOff,
  MessageSquare,
  Send,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { FEEDBACK_IGNORE_ATTRIBUTE } from "@shared-core/capabilities/feedback-capture";
import { objectOnPage } from "@shared-core/domain/feedback-quick-note";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { useQuickNote } from "@shared-core/domain/use-quick-note";
import type { ConversationMessage } from "@shared-core/data/api-types";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../primitives/alert-dialog";
import { useIsMobile } from "../../primitives/use-mobile";
import { currentTime } from "@shared-core/domain/current-time";
import {
  readPreviewContext,
  type PreviewContext,
} from "../../patterns/previewContext";
import { buildFeedbackContext } from "./feedbackContext";
import { QuickNotePanel } from "./QuickNotePanel";

type Status = "idle" | "streaming" | "submitting" | "previewing";
type View = "chat" | "preview" | "submitted";
type FeedbackMode = "feedback" | "brain_sucker";
type Score = { themes: number; ideas: number };
type Summary = { headline: string; text: string };

// Props the parent passes to each view so the header acts as a drag handle
// for repositioning the dialog. The header div spreads these.
type DragHandleProps = {
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => void;
  style: React.CSSProperties;
};

const MODE_CONFIG = {
  feedback: {
    title: "Feedback",
    description: "Share what's on your mind — we'll talk it through.",
    placeholder: "Type your feedback...",
    successToast: "Thanks — your feedback has been submitted",
    emptyMessage:
      "Type your feedback below — I'll help you develop your thoughts.",
    submittedHeadingNoun: "feedback",
  },
  brain_sucker: {
    title: "Brain Sucker",
    description:
      "Let me extract every idea, problem, and dream from your head.",
    placeholder:
      "What's on your mind? Problems, ideas, frustrations, dreams — anything goes...",
    successToast: "Thanks — your ideas have been captured",
    emptyMessage:
      "Tell me what's bugging you, what you dream about, what you'd fix with a magic wand — I'll keep pulling ideas out of your head.",
    submittedHeadingNoun: "ideas",
  },
} as const;

export interface FeedbackWidgetProps {
  /** Open on the quick note, as if the person had pressed the Feedback button. */
  defaultOpen?: boolean;
  /** Open straight into a conversation mode (Talk it through, or Brain sucker). */
  defaultMode?: FeedbackMode;
}

/** The preview a note was raised in: a testing preview's environment id, else the branch it was built from. */
function previewIdOf(preview: PreviewContext | null): string | null {
  return preview ? (preview.environmentId ?? preview.branch) : null;
}

/** The shortcut that opens the quick note from anywhere: a modifier, Shift and F. */
function isQuickNoteShortcut(event: {
  key: string;
  hasModifier: boolean;
  hasShift?: boolean;
}): boolean {
  return (
    event.hasModifier &&
    event.hasShift === true &&
    event.key.toLowerCase() === "f"
  );
}

// crypto.randomUUID is available in all browsers we ship to; the
// capability port keeps us platform-neutral and testable.
function newSessionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for the very-rare case crypto.randomUUID is missing — good
  // enough to key a single in-flight draft. Not security-sensitive.
  return `s-${currentTime().getTime().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function FeedbackWidget({
  defaultOpen = false,
  defaultMode,
}: FeedbackWidgetProps) {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const isMobile = useIsMobile();
  // `open` is the conversation dialog; the quick note is its own panel beside it.
  const [open, setOpen] = useState(defaultMode !== undefined);
  const [isQuickNoteOpen, setIsQuickNoteOpen] = useState(defaultOpen);
  const [mode, setMode] = useState<FeedbackMode | null>(defaultMode ?? null);
  const quickNote = useQuickNote({
    previewId: previewIdOf(readPreviewContext()),
  });
  const beginQuickNote = quickNote.begin;
  // The screenshot is drawn as the panel opens (before it paints), not when it first renders.
  const openQuickNote = useCallback(() => {
    beginQuickNote();
    setIsQuickNoteOpen(true);
  }, [beginQuickNote]);
  const [view, setView] = useState<View>("chat");
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [streamingText, setStreamingText] = useState("");
  const [score, setScore] = useState<Score | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  // Session id is generated lazily on the first turn and cleared when
  // the widget closes after submit or discard. Held in a ref so it
  // survives renders without triggering re-renders of its own.
  const sessionIdRef = useRef<string | null>(null);

  function ensureSessionId(): string {
    if (sessionIdRef.current === null) {
      sessionIdRef.current = newSessionId();
    }
    return sessionIdRef.current;
  }

  function clearSessionId(): void {
    sessionIdRef.current = null;
  }

  const hasConversation = messages.length > 0;

  useEffect(() => {
    if (defaultOpen) beginQuickNote();
    // Only the first render: later opens go through the button and the shortcut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return capabilities.registerGlobalKeydownListener((event) => {
      if (!isQuickNoteShortcut(event) || open || isQuickNoteOpen) return;
      event.preventDefault();
      openQuickNote();
    });
  }, [capabilities, open, isQuickNoteOpen, openQuickNote]);

  /** Talk it through and Brain sucker: the panel gives way to the conversation, or resumes a draft in its own mode. */
  function startConversation(selected: FeedbackMode) {
    if (!hasConversation) {
      setMode(selected);
      setView("chat");
    }
    setIsQuickNoteOpen(false);
    setOpen(true);
  }

  function toggleQuickNote() {
    if (isQuickNoteOpen) setIsQuickNoteOpen(false);
    else openQuickNote();
  }
  const canSend = input.trim().length > 0 && status === "idle" && mode !== null;
  const canPreview = hasConversation && status === "idle";
  const canSubmit = hasConversation && status === "idle";
  const config = mode ? MODE_CONFIG[mode] : null;

  // On desktop the dialog opens centred, then immediately switches to
  // absolute positioning at its rendered location so drag and resize
  // behave naturally. On mobile we skip all of that and render the
  // dialog fullscreen — drag/resize don't make sense on touch and a
  // floating window would just overlap the on-screen keyboard.
  const contentRef = useRef<HTMLDivElement>(null);
  const [dialogPos, setDialogPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [hasMoved, setHasMoved] = useState(false);
  const dragOriginRef = useRef<{
    mouseX: number;
    mouseY: number;
    posX: number;
    posY: number;
  } | null>(null);

  const dialogClasses = isMobile
    ? // Fullscreen-style on mobile: override the primitive's centring
      // translate, fill the viewport, no rounded corners, no resize.
      "inset-0 top-0 left-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0 flex flex-col p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] overflow-hidden"
    : `resize overflow-hidden flex flex-col p-0 ${
        mode === "brain_sucker"
          ? "sm:w-[44rem] sm:max-w-[95vw] h-[80vh] max-h-[92vh] min-h-[24rem] min-w-[20rem]"
          : "sm:max-w-lg w-full max-h-[80vh] min-h-[20rem] min-w-[20rem]"
      }`;
  const dialogStyle =
    !isMobile && dialogPos
      ? {
          left: `${dialogPos.x}px`,
          top: `${dialogPos.y}px`,
          // Both are needed to undo the primitive's centring. Tailwind v4
          // compiles `translate-x/y-*` to the standalone `translate` CSS
          // property, NOT to `transform` — so `transform: none` alone leaves
          // `translate: -50% -50%` in force and the dialog renders half its
          // own height above the `top` we just pinned it to. The taller the
          // view, the bigger the shift, so switching to a taller view walks
          // the dialog off the top of the screen and puts its footer buttons
          // out of reach (the dialog is `fixed`, so nothing can scroll to it).
          transform: "none",
          translate: "none",
        }
      : undefined;

  // Reset positioning when the dialog closes so the next open re-centres.
  useEffect(() => {
    if (!open) {
      setDialogPos(null);
      setHasMoved(false);
    }
  }, [open]);

  // After each render that changes the dialog's natural size (open, mode,
  // view), measure its rendered position and lock to absolute coordinates.
  // Skip if the user has manually dragged — they own the position then.
  // Skip entirely on mobile: the dialog is fullscreen with no drag/resize.
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    if (hasMoved) return;
    const el = contentRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = { x: Math.round(rect.left), y: Math.round(rect.top) };
    setDialogPos((prev) =>
      prev && prev.x === next.x && prev.y === next.y ? prev : next,
    );
  }, [open, mode, view, hasMoved, isMobile]);

  const handleHeaderPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only initiate drag from non-interactive surface — let buttons,
      // links, inputs, and the dialog close X handle their own clicks.
      const target = e.target as HTMLElement;
      if (target.closest("button, a, input, textarea, [role='button']")) return;
      if (!dialogPos) return;
      dragOriginRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        posX: dialogPos.x,
        posY: dialogPos.y,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [dialogPos],
  );

  const handleHeaderPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const origin = dragOriginRef.current;
      if (!origin) return;
      const dx = e.clientX - origin.mouseX;
      const dy = e.clientY - origin.mouseY;
      // Clamp the dialog to keep at least 80px on-screen so it can always
      // be grabbed back if the user drags it most of the way off-viewport.
      const el = contentRef.current;
      const w = el?.offsetWidth ?? 320;
      const viewport = capabilities.getViewportSize();
      const minX = 80 - w;
      const maxX = viewport.width - 80;
      const minY = 0;
      const maxY = viewport.height - 40;
      const nextX = Math.min(maxX, Math.max(minX, origin.posX + dx));
      const nextY = Math.min(maxY, Math.max(minY, origin.posY + dy));
      setHasMoved(true);
      setDialogPos({ x: nextX, y: nextY });
    },
    [capabilities],
  );

  const handleHeaderPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      dragOriginRef.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    [],
  );

  // On mobile the dialog is fullscreen and not draggable, so the
  // header is just a header — no cursor change, no pointer capture,
  // no swallowing of vertical scroll gestures.
  const dragHandleProps: DragHandleProps = isMobile
    ? {
        onPointerDown: () => {},
        onPointerMove: () => {},
        onPointerUp: () => {},
        onPointerCancel: () => {},
        style: {},
      }
    : {
        onPointerDown: handleHeaderPointerDown,
        onPointerMove: handleHeaderPointerMove,
        onPointerUp: handleHeaderPointerUp,
        onPointerCancel: handleHeaderPointerUp,
        style: { cursor: "move", touchAction: "none" },
      };

  function resetConversationState() {
    setMessages([]);
    setInput("");
    setStreamingText("");
    setScore(null);
    setSummary(null);
    setStatus("idle");
    setView("chat");
  }

  const handleSend = useCallback(async () => {
    if (!canSend || !mode) return;
    const userMessage: ConversationMessage = {
      role: "user",
      content: input.trim(),
    };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setStatus("streaming");
    setStreamingText("");

    try {
      const stream = await adapter.respondToFeedback({
        messages: updatedMessages,
        page_path: capabilities.getCurrentPath(),
        mode,
        session_id: ensureSessionId(),
      });

      const reader = stream.getReader();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value.type === "text") {
          accumulated += value.content;
          setStreamingText(accumulated);
        } else if (value.type === "score") {
          setScore({ themes: value.themes, ideas: value.ideas });
        } else if (value.type === "error") {
          throw new Error("stream_error");
        }
        // "done" closes the stream from the server side; the reader
        // surfaces it as the next ``done: true`` and we exit the loop.
      }

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: accumulated },
      ]);
      setStreamingText("");
      setStatus("idle");
    } catch (err) {
      console.error("feedback.conversation.stream_failed", err);
      toast.error("Couldn't get a response. Please try again.");
      setStreamingText("");
      setStatus("idle");
    }
  }, [canSend, input, messages, adapter, capabilities, mode]);

  const handlePreview = useCallback(async () => {
    if (!canPreview || !mode) return;
    setStatus("previewing");
    try {
      const result = await adapter.previewFeedback({ messages, mode });
      setSummary({
        headline: result.summary_headline,
        text: result.summary_text,
      });
      setView("preview");
      setStatus("idle");
      // Dismiss the close-confirm if Preview was reached from there.
      setShowCloseConfirm(false);
    } catch (err) {
      console.error("feedback.preview.failed", err);
      toast.error("Couldn't generate a preview. Please try again.");
      setStatus("idle");
    }
  }, [canPreview, messages, adapter, mode]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !mode) return;
    setStatus("submitting");

    try {
      const result = await adapter.submitFeedback({
        conversation_thread: messages,
        page_path: capabilities.getCurrentPath(),
        context: buildFeedbackContext(
          readPreviewContext(),
          capabilities.getCurrentPath(),
          currentTime(),
        ),
        mode,
        session_id: sessionIdRef.current,
      });
      setSummary({
        headline: result.summary_headline,
        text: result.summary_text,
      });
      setView("submitted");
      setStatus("idle");
      setShowCloseConfirm(false);
      // Backend has promoted the draft (or noticed there was none) —
      // clear the local id so the next conversation gets a fresh one.
      clearSessionId();
      toast.success(MODE_CONFIG[mode].successToast);
    } catch (err) {
      console.error("feedback.submit.failed", err);
      toast.error("Couldn't submit. Please try again.");
      setStatus("idle");
    }
  }, [canSubmit, messages, adapter, capabilities, mode]);

  const handleDiscard = useCallback(() => {
    // Fire-and-forget the server-side discard so we don't keep a draft
    // around for a conversation the user explicitly threw away. We
    // don't await — the UX closes immediately. Server treats unknown
    // sessions as no-op.
    const sid = sessionIdRef.current;
    if (sid !== null) {
      adapter.discardFeedbackDraft(sid).catch((err) => {
        console.warn("feedback.draft.discard_failed", err);
      });
    }
    clearSessionId();
    resetConversationState();
    setMode(null);
    setShowCloseConfirm(false);
    setOpen(false);
  }, [adapter]);

  const handleDone = useCallback(() => {
    clearSessionId();
    resetConversationState();
    setMode(null);
    setOpen(false);
  }, []);

  const handleClear = useCallback(() => {
    // "Clear" wipes the in-flight conversation — treat the existing
    // draft as discarded so we don't leave server-side state behind.
    const sid = sessionIdRef.current;
    if (sid !== null) {
      adapter.discardFeedbackDraft(sid).catch((err) => {
        console.warn("feedback.draft.discard_failed", err);
      });
    }
    clearSessionId();
    resetConversationState();
  }, [adapter]);

  const handleAddMore = useCallback(() => {
    setView("chat");
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  function handleOpenChange(next: boolean) {
    if (status === "submitting" || status === "previewing") return;
    if (!next && view === "submitted") {
      // Already submitted — closing is just confirming.
      handleDone();
      return;
    }
    if (!next && hasConversation) {
      setShowCloseConfirm(true);
      return;
    }
    if (!next && !hasConversation) {
      setMode(null);
      setView("chat");
    }
    setOpen(next);
  }

  /** Back from an empty conversation to the quick note. */
  function handleBack() {
    if (hasConversation) return;
    setMode(null);
    setOpen(false);
    setIsQuickNoteOpen(true);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        type="button"
        onClick={toggleQuickNote}
        aria-label="Send feedback"
        aria-expanded={isQuickNoteOpen}
        {...{ [FEEDBACK_IGNORE_ATTRIBUTE]: "" }}
        // A phone gets the round icon alone: the pill would sit over the page.
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))] z-50 h-12 gap-2 rounded-full shadow-lg max-sm:bottom-[max(1rem,env(safe-area-inset-bottom))] max-sm:right-[max(1rem,env(safe-area-inset-right))] max-sm:w-12 max-sm:px-0"
      >
        <MessageSquare className="size-4" aria-hidden />
        <span className="text-sm font-medium max-sm:sr-only">Feedback</span>
        {hasConversation && !open && (
          <span className="absolute -top-1 -right-1 size-3 rounded-full bg-blue-500" />
        )}
      </Button>
      {isQuickNoteOpen && (
        <QuickNoteWithObjectLabel
          note={quickNote}
          onClose={() => setIsQuickNoteOpen(false)}
          onTalkItThrough={() => startConversation("feedback")}
          onBrainSucker={() => startConversation("brain_sucker")}
          hasConversationDraft={hasConversation}
        />
      )}
      <DialogContent
        ref={contentRef}
        className={dialogClasses}
        style={dialogStyle}
      >
        {mode === null ? null : view === "preview" ? (
          <PreviewView
            mode={mode}
            summary={summary}
            score={score}
            messageCount={messages.length}
            onAddMore={handleAddMore}
            onSubmit={handleSubmit}
            submitting={status === "submitting"}
            dragHandleProps={dragHandleProps}
          />
        ) : view === "submitted" ? (
          <SubmittedView
            mode={mode}
            summary={summary}
            onDone={handleDone}
            dragHandleProps={dragHandleProps}
          />
        ) : (
          <ChatView
            mode={mode}
            config={config!}
            hasConversation={hasConversation}
            messages={messages}
            streamingText={streamingText}
            input={input}
            status={status}
            score={score}
            canSend={canSend}
            canPreview={canPreview}
            canSubmit={canSubmit}
            onBack={handleBack}
            onInputChange={setInput}
            onKeyDown={handleKeyDown}
            onSend={handleSend}
            onPreview={handlePreview}
            onSubmit={handleSubmit}
            onClear={handleClear}
            dragHandleProps={dragHandleProps}
          />
        )}
      </DialogContent>
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Before you close...</AlertDialogTitle>
            <AlertDialogDescription>
              Your draft hasn&apos;t been submitted yet. You can preview the
              auto-summary, submit it now, or discard it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel>Keep going</AlertDialogCancel>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              className="gap-1.5"
            >
              <Trash2 className="size-3.5" />
              Discard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={status !== "idle"}
              className="gap-1.5"
            >
              <Eye className="size-3.5" />
              Preview
            </Button>
            <AlertDialogAction onClick={handleSubmit} className="gap-1.5">
              <Upload className="size-3.5" />
              Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

/**
 * The panel, told the words for the page's object ("Orders"). Only drawn while the panel is open, so a page without
 * the object manifest (a test of a layout) never needs it.
 */
function QuickNoteWithObjectLabel(
  props: Omit<React.ComponentProps<typeof QuickNotePanel>, "objectLabel">,
) {
  const manifest = useObjectManifest();
  const pageObject = objectOnPage(props.note.capture?.pagePath ?? "").objectId;
  const objectLabel =
    pageObject !== null && manifest.isObjectKnown(pageObject)
      ? manifest.objectOf(pageObject).plural
      : null;
  return <QuickNotePanel {...props} objectLabel={objectLabel} />;
}

// --- Chat view -------------------------------------------------------------

interface ChatViewProps {
  mode: FeedbackMode;
  config: (typeof MODE_CONFIG)[FeedbackMode];
  hasConversation: boolean;
  messages: ConversationMessage[];
  streamingText: string;
  input: string;
  status: Status;
  score: Score | null;
  canSend: boolean;
  canPreview: boolean;
  canSubmit: boolean;
  onBack: () => void;
  onInputChange: (next: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  onPreview: () => void;
  onSubmit: () => void;
  onClear: () => void;
  dragHandleProps: DragHandleProps;
}

function ChatView(props: ChatViewProps) {
  const {
    mode,
    config,
    hasConversation,
    messages,
    streamingText,
    input,
    status,
    score,
    canSend,
    canPreview,
    canSubmit,
    onBack,
    onInputChange,
    onKeyDown,
    onSend,
    onPreview,
    onSubmit,
    onClear,
    dragHandleProps,
  } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll the message list to the latest content as it grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, streamingText]);

  // Auto-grow the textarea up to its max-height.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [input]);

  return (
    <>
      <DialogHeader {...dragHandleProps} className="px-4 sm:px-6 pt-6 pb-2">
        <div className="flex items-center gap-2">
          {!hasConversation && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              onClick={onBack}
              aria-label="Back to quick note"
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          <div className="flex-1">
            <DialogTitle className="flex items-center gap-2">
              {mode === "brain_sucker" && (
                <Brain className="size-4 text-purple-500" />
              )}
              {config.title}
            </DialogTitle>
            <DialogDescription>{config.description}</DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Draft banner + top action cluster */}
      {hasConversation && (
        <div className="px-4 sm:px-6 pt-2 pb-1 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <EyeOff className="size-3.5 shrink-0" aria-hidden />
            <span className="hidden sm:inline">
              Draft — only you can see this. Submit when ready.
            </span>
            <span className="sm:hidden">Draft</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {score && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                aria-label={`${score.themes} themes, ${score.ideas} ideas`}
              >
                <Sparkles
                  className="size-3 text-purple-500 shrink-0"
                  aria-hidden
                />
                {/* Compact form on mobile, expanded on desktop. */}
                <span className="hidden sm:inline">
                  {score.themes} {score.themes === 1 ? "theme" : "themes"} ·{" "}
                  {score.ideas} {score.ideas === 1 ? "idea" : "ideas"}
                </span>
                <span className="sm:hidden">
                  {score.themes}·{score.ideas}
                </span>
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onPreview}
              disabled={!canPreview}
              aria-label="Preview auto-summary"
              className="gap-1.5 h-7 px-2.5"
            >
              <Eye className="size-3.5" aria-hidden />
              {status === "previewing" ? "Previewing..." : "Preview"}
            </Button>
            <Button
              size="sm"
              onClick={onSubmit}
              disabled={!canSubmit}
              aria-label="Submit feedback"
              className="gap-1.5 h-7 px-2.5"
            >
              <Upload className="size-3.5" aria-hidden />
              {status === "submitting" ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      )}

      {/* Message area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3 min-h-[200px]"
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {streamingText && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap bg-muted text-foreground">
              {streamingText}
              <span className="inline-block w-1.5 h-4 bg-foreground/50 animate-pulse ml-0.5 align-text-bottom" />
            </div>
          </div>
        )}
        {messages.length === 0 && !streamingText && (
          <div className="text-center text-muted-foreground text-sm py-8">
            {config.emptyMessage}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="border-t px-4 sm:px-6 pt-4 pb-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={config.placeholder}
            rows={2}
            disabled={status !== "idle"}
            className="flex-1 resize-none overflow-y-auto rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 min-h-[3.5rem] max-h-40"
          />
          <Button
            size="icon"
            onClick={onSend}
            disabled={!canSend}
            aria-label="Send message"
          >
            <Send className="size-4" />
          </Button>
        </div>
        <div className="mt-3 flex justify-end">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasConversation || status !== "idle"}
                className="text-muted-foreground gap-1.5"
              >
                <Trash2 className="size-3.5" aria-hidden />
                Clear
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Discard this conversation?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will clear the messages above and your{" "}
                  {mode === "brain_sucker" ? "ideas" : "feedback"} will be lost.
                  You can&apos;t undo this.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onClear}>Discard</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </>
  );
}

// --- Preview view ----------------------------------------------------------

interface PreviewViewProps {
  mode: FeedbackMode;
  summary: Summary | null;
  score: Score | null;
  messageCount: number;
  onAddMore: () => void;
  onSubmit: () => void;
  submitting: boolean;
  dragHandleProps: DragHandleProps;
}

function PreviewView({
  mode,
  summary,
  score,
  messageCount,
  onAddMore,
  onSubmit,
  submitting,
  dragHandleProps,
}: PreviewViewProps) {
  return (
    <>
      <DialogHeader {...dragHandleProps} className="px-4 sm:px-6 pt-6 pb-2">
        <DialogTitle className="flex items-center gap-2">
          {mode === "brain_sucker" && (
            <Brain className="size-4 text-purple-500" />
          )}
          Preview &middot; your draft
        </DialogTitle>
        <DialogDescription>
          This is the auto-summary that will be persisted on submit. Add more if
          something feels missing.
        </DialogDescription>
      </DialogHeader>

      {score && (
        <div className="px-4 sm:px-6 pb-2">
          <span
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
            aria-label={`${score.themes} themes, ${score.ideas} ideas`}
          >
            <Sparkles className="size-3 text-purple-500 shrink-0" aria-hidden />
            <span className="hidden sm:inline">
              {score.themes} {score.themes === 1 ? "theme" : "themes"} ·{" "}
              {score.ideas} {score.ideas === 1 ? "idea" : "ideas"}
            </span>
            <span className="sm:hidden">
              {score.themes}·{score.ideas}
            </span>
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {summary ? (
          <div className="space-y-3">
            <h3 className="text-base font-semibold leading-tight">
              {summary.headline}
            </h3>
            <SummaryMarkdown text={summary.text} />
            <details className="text-xs text-muted-foreground pt-3 border-t">
              <summary className="cursor-pointer select-none">
                Original conversation ({messageCount}{" "}
                {messageCount === 1 ? "message" : "messages"})
              </summary>
            </details>
          </div>
        ) : (
          <div className="text-center text-muted-foreground text-sm py-8">
            No summary available.
          </div>
        )}
      </div>

      <div className="border-t px-4 sm:px-6 py-3 flex justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onAddMore}
          className="gap-1.5"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Add more
        </Button>
        <Button
          size="sm"
          onClick={onSubmit}
          disabled={submitting}
          className="gap-1.5"
        >
          <Upload className="size-3.5" aria-hidden />
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </div>
    </>
  );
}

// --- Submitted view --------------------------------------------------------

interface SubmittedViewProps {
  mode: FeedbackMode;
  summary: Summary | null;
  onDone: () => void;
  dragHandleProps: DragHandleProps;
}

function SubmittedView({
  mode,
  summary,
  onDone,
  dragHandleProps,
}: SubmittedViewProps) {
  const config = MODE_CONFIG[mode];
  return (
    <>
      <DialogHeader {...dragHandleProps} className="px-4 sm:px-6 pt-6 pb-2">
        <DialogTitle className="flex items-center gap-2">
          {mode === "brain_sucker" && (
            <Brain className="size-4 text-purple-500" />
          )}
          Submitted
        </DialogTitle>
        <DialogDescription>
          Thanks — your {config.submittedHeadingNoun} have been captured.
        </DialogDescription>
      </DialogHeader>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {summary ? (
          <div className="space-y-3">
            <div className="text-sm font-medium text-green-700 dark:text-green-500">
              ✓ Submitted
            </div>
            <h3 className="text-base font-semibold leading-tight">
              {summary.headline}
            </h3>
            <SummaryMarkdown text={summary.text} />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">✓ Submitted.</div>
        )}
      </div>

      <div className="border-t px-4 sm:px-6 py-3 flex justify-end">
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </>
  );
}

// --- Markdown-ish renderer -------------------------------------------------
//
// The summary text follows a tiny convention: ``## `` lines are theme
// headings, ``- `` lines are ideas, blank lines separate sections.
// We render that without pulling in a full Markdown parser.

function SummaryMarkdown({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let currentList: string[] = [];

  function flushList(key: string) {
    if (currentList.length === 0) return;
    blocks.push(
      <ul key={key} className="list-disc pl-5 space-y-1 text-sm">
        {currentList.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>,
    );
    currentList = [];
  }

  const lines = text.split("\n");
  lines.forEach((line, i) => {
    if (line.startsWith("## ")) {
      flushList(`list-${i}`);
      blocks.push(
        <h4
          key={`h-${i}`}
          className="text-sm font-semibold pt-2 first:pt-0 text-foreground"
        >
          {line.slice(3)}
        </h4>,
      );
    } else if (line.startsWith("- ")) {
      currentList.push(line.slice(2));
    } else if (line.trim() === "") {
      flushList(`list-${i}`);
    } else {
      flushList(`list-${i}`);
      blocks.push(
        <p key={`p-${i}`} className="text-sm leading-relaxed">
          {line}
        </p>,
      );
    }
  });
  flushList("list-final");

  return <div className="space-y-2">{blocks}</div>;
}
